/* eslint-disable no-undef */
import { OrderItem, OrderTransaction, OrderTransactionType, Team, UserAccount } from '@reg/shared/model';
import { OrderService } from '@reg/shared/service';
import { ISelectedPaymentOptions } from '@reg/event/registration/payment/payment-item.model';
import get from 'lodash-es/get';
import { zonedTimeToUtc } from 'date-fns-tz';

export enum OrderStatus {
    PAID = 'paid',
    UNPAID = 'unpaid',
    PAY_ON_SITE = 'pay on-site',
    INVALID = 'invalid',
    REFUNDED = 'refunded',
    NA = 'na',
}

/**
 * Model to hold Order details used for payment
 */
export class Order {
    public static ENTITY_NAME: string = 'Order';

    public id: string;
    public eventId: string;
    public team: Team;
    public userAccount: UserAccount;
    public status: string;
    public total: number;
    public paidAmount: number;
    public onlineTotalDue: number;
    public onsiteTotalDue: number;
    public orderItems: OrderItem[];
    public orderTransactions: OrderTransaction[];
    public createdDateTime: Date;

    public get teamId(): string {
        return (this.team && this.team.id) || null;
    }

    public get userId(): string {
        return (this.userAccount && this.userAccount.id) || null;
    }

    get isPaid(): boolean {
        return this.status === OrderStatus.PAID;
    }

    get isUnpaid(): boolean {
        return this.status === OrderStatus.UNPAID;
    }

    get isPayOnsite(): boolean {
        return this.status === OrderStatus.PAY_ON_SITE;
    }

    get isInvalid(): boolean {
        return this.status === OrderStatus.INVALID;
    }

    get isRefunded(): boolean {
        return this.status === OrderStatus.REFUNDED;
    }

    get isNA(): boolean {
        return this.status === OrderStatus.NA;
    }

    public getOrderItemsByStatus(status: string): OrderItem[] {
        if (this.orderItems) {
            return this.orderItems.filter((x: OrderItem) => x.status === status);
        }

        return [];
    }

    public get refundedTotal(): number {
        let refunded: number = 0;

        (this.orderTransactions || [])
            .filter((ot: OrderTransaction) =>
                ot.isStatusSucceeded && ot.transactionType === OrderTransactionType.REFUND)
            .forEach((ot: OrderTransaction) => { refunded += ot.amount; });

        return refunded;
    }

    public hasPaidOrPayOnSiteItem(): boolean {
        return (
            this.getOrderItemsByStatus(OrderStatus.PAID).length > 0 ||
            this.getOrderItemsByStatus(OrderStatus.PAY_ON_SITE).length > 0
        );
    }

    public isEqual(order: Order): boolean {
        if (this.id !== order.id ||
            this.eventId !== order.eventId ||
            this.teamId !== order.teamId ||
            this.status !== order.status ||
            this.total !== order.total ||
            this.onlineTotalDue !== order.onlineTotalDue ||
            this.onsiteTotalDue !== order.onsiteTotalDue) {
            return false;
        }

        if ((this.orderItems && !order.orderItems) ||
            (!this.orderItems && order.orderItems)) {
            return false;
        }

        // Compare the order items
        if (this.orderItems && order.orderItems) {
            if (this.orderItems.length !== order.orderItems.length) {
                return false;
            }

            for (let idx: number = 0; idx < this.orderItems.length; idx++) {
                const oi: OrderItem = this.orderItems[idx];

                const match: OrderItem = order.orderItems.filter((x: OrderItem) => x.id === oi.id)[0];

                if (!match ||
                    oi.entityGuid !== match.entityGuid ||
                    oi.status !== match.status ||
                    oi.onlinePrice !== match.onlinePrice ||
                    oi.onsitePrice !== match.onsitePrice ||
                    oi.quantity !== match.quantity ||
                    oi.productTypeId !== match.productTypeId) {
                    return false;
                }
            }
        }

        return true;
    }

    public getPaidDate(): Date {
        if (this.orderTransactions) {
            // Sort the transactions by dateTime
            const sortedArray: OrderTransaction[] = this.orderTransactions.sort(
                (n1: OrderTransaction, n2: OrderTransaction) => {
                    if (n1.dateTime < n2.dateTime) {
                        return 1;
                    }

                    if (n1.dateTime > n2.dateTime) {
                        return -1;
                    }

                    return 0;
                },
            );

            // Grab the most recent succeeded transaction
            const lastSuccess: OrderTransaction = sortedArray.filter(
                (t: OrderTransaction) => t.amount > 0 &&
                    t.transactionType === OrderTransactionType.CHARGE &&
                    t.status === OrderService.ORDER_TRANSACTION_STATUS_SUCCEEDED,
            )[0];

            if (lastSuccess) {
                return lastSuccess.dateTime;
            }
        }

        return null;
    }

    /**
     * Serialize to a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'order';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.relationships = {};

        const event: any = {};
        event.data = { type: 'event', id: this.eventId };
        json.data.relationships.event = event;

        if (this.teamId) {
            const team: any = {};
            team.data = { type: 'team', id: this.teamId };
            json.data.relationships.team = team;
        }

        return JSON.stringify(json);
    }

    public serializeForCoupon(couponCode: string, selectedPaymentOptions: ISelectedPaymentOptions): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'order';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.couponCode = couponCode;
        json.data.attributes.selectedPaymentOptions = selectedPaymentOptions;

        return JSON.stringify(json);
    }

    /**
     * Serialize for submitting a payment
     */
    public serializeForPayment(
        eventName: string,
        createTokenResponse: stripe.Token,
        payOnlineItems: OrderItem[],
        payOnsiteItems: OrderItem[],
    ): string {
        const json: any = {};
        json.data = {};
        json.data.attributes = {};
        json.data.id = this.id;
        json.data.type = 'order';
        const chargeRequest: any = {};
        chargeRequest.onlineAmount = 0;
        chargeRequest.utcOffsetHours = (new Date()).getTimezoneOffset() / 60;
        chargeRequest.currency = 'usd';
        chargeRequest.description = `${eventName} Registration`;
        chargeRequest.payOnlineItems = [];
        chargeRequest.payOnsiteItems = [];

        // Append the order items to pay online
        if (payOnlineItems) {
            payOnlineItems.forEach((item: OrderItem) => {
                chargeRequest.payOnlineItems.push({ id: item.id });
                chargeRequest.onlineAmount += item.onlineSubtotal;
            });
        }

        // Append the order items to pay onsite
        if (payOnsiteItems) {
            payOnsiteItems.forEach((item: OrderItem) => {
                chargeRequest.payOnsiteItems.push({ id: item.id });
            });
        }

        chargeRequest.token = createTokenResponse;
        json.data.attributes = chargeRequest;

        return json;
    }

    /**
     * Deserialize from a json api format
     */
    public deserialize(json: any, included: any[]): Order {
        const data: any = json.data || json;
        const attrs: any = data.attributes;
        const relationships: any = data.relationships || {};
        included = included || [];

        this.id = data.id;
        this.eventId = attrs.eventId;
        this.status = attrs.status;
        this.total = attrs.total;
        this.paidAmount = attrs.paidAmount;
        this.onlineTotalDue = attrs.onlineTotalDue;
        this.onsiteTotalDue = attrs.onsiteTotalDue;

        if (attrs.createdDateTime) {
            const localTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
            this.createdDateTime = zonedTimeToUtc(attrs.createdDateTime, localTimezone);
        }

        // Fill Team from relationships if it exists
        const teamId: string = get(data, 'relationships.team.data.id');
        const team: any = included.find((i: any) => i.type === 'team' && i.id === teamId);

        if (team) {
            this.team = (new Team(attrs.eventId, attrs.teamId, false)).deserialize(team);
        } else if (attrs.teamId) {
            this.team = new Team(attrs.eventId, attrs.teamId, false);
        }

        // Fill UserAccount from relationships if it exists
        const userId: string = get(data, 'relationships.userAccount.data.id');
        const userAccount: any = included.find((i: any) => i.type === 'userAccount' && i.id === userId);

        if (userAccount) {
            this.userAccount = (new UserAccount()).deserialize(userAccount);
        } else if (attrs.userId) {
            this.userAccount = new UserAccount(attrs.userId);
        }

        // Fill OrderItems from relationships if it exists
        this.orderItems = [];

        const orderItemLinks: any = relationships.orderItems;
        if (orderItemLinks &&
            included &&
            orderItemLinks.data) {
            const childOrderItems: OrderItem[] = [];

            for (const orderItemLink of orderItemLinks.data) {
                const orderItem: any = included.filter((i: any) => i.id === orderItemLink.id)[0];
                if (orderItem) {
                    const oi: OrderItem = (new OrderItem()).deserialize(orderItem, included);

                    if (oi.parentId) {
                        childOrderItems.push(oi);
                    } else {
                        this.orderItems.push(oi);
                    }
                }
            }

            // Set the order item children
            this.orderItems.forEach((oi: OrderItem) => {
                oi.children = childOrderItems.filter((x: OrderItem) => x.parentId === oi.id);
            });
        }

        // Fill OrderTransactions from relationships if it exists
        const orderTranLinks: any = relationships.orderTransactions;
        if (orderTranLinks &&
            included &&
            orderTranLinks.data) {
            this.orderTransactions = [];

            for (const orderTranId of orderTranLinks.data) {
                const transaction: any = included.filter((i: any) => i.id === orderTranId.id)[0];
                if (transaction) {
                    this.orderTransactions.push((new OrderTransaction()).deserialize(transaction));
                }
            }
        }

        return this;
    }
}
